// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Haggle — the play surface.
//
// ── THE ACTION IS INFERRED, NOT CHOSEN ───────────────────────────────────────────────────────────
//
// There is no mode, no modal and no drag. A tap on a berth marks a card IN; a tap on a satchel stack
// or the gang marks cards OUT; and the action falls out of the two counts:
//
//   one berth in, nothing out ................ TAKE
//   nothing in, N of one ware out ............ SELL
//   k berths in, k cards out (k >= 2) ........ BARTER
//   a porter berth ........................... HIRE (always every porter — that is the rule)
//
// So a single tap is still a single-tap TAKE, and the game's one fiddly action assembles itself out
// of the same gesture with nothing new to learn.
//
// ── THE BUTTON IS A LIVE QUOTE, AND IT IS SHOWN ON NOTHING-TURNS TOO ─────────────────────────────
//
// With no selection at all the bar still reads `SELL 2 COPPER -> 14`, greyed. Temptation IS the
// game: the whole point is that you can see, every single turn, exactly what walking away from a
// sale is costing you. Hiding that on the turns where the player has not touched anything would hide
// it on precisely the turns where it does the most work.

import {
  BERTHS,
  MIN_SALE,
  PORTER,
  SATCHEL_LIMIT,
  TARIFF,
  WARES,
  WARE_NAME,
  bountyTierFor,
  isWare,
  type Card,
  type Ware,
} from './wares';
import { WARE_GLYPH } from './palette';
import {
  countOf,
  doomedChit,
  dryCount,
  legal,
  other,
  quote,
  scoreOf,
  sum,
  trestlePorters,
  type Action,
  type PublicState,
  type Seat,
} from './rules';
import { provableRead } from './view';

export interface Sel {
  berths: number[];
  hire: boolean;
  ware: Ware | null;
  n: number;
  porters: number;
}

export const emptySel = (): Sel => ({ berths: [], hire: false, ware: null, n: 0, porters: 0 });

export interface TableDeps {
  submit: (a: Action) => void;
  sfx: (name: string) => void;
  onQuit: () => void;
  onHelp: () => void;
}

export interface RenderArgs {
  p: PublicState;
  you: Seat;
  satchel: readonly Ware[];
  names: readonly string[];
  pennants: readonly [number, number];
  /** True when this client is only watching (spectator, or the rival's turn). */
  yourTurn: boolean;
  note: string;
}

/** What the current selection means, as a legal action or an instruction. */
export interface Quote {
  action: Action | null;
  label: string;
  advisory: boolean;
}

export function readSelection(
  p: PublicState,
  seat: Seat,
  satchel: readonly Ware[],
  sel: Sel,
): Quote {
  const outCards = sel.n + sel.porters;
  if (sel.hire) {
    const n = trestlePorters(p);
    return { action: { k: 'hire' }, label: `HIRE ${n} PORTER${n === 1 ? '' : 'S'}`, advisory: false };
  }
  if (sel.berths.length === 1 && outCards === 0) {
    const w = p.trestle[sel.berths[0]];
    if (w !== null && isWare(w)) {
      if (satchel.length >= SATCHEL_LIMIT)
        return { action: null, label: 'SATCHEL FULL — sell or barter', advisory: true };
      return { action: { k: 'take', berth: sel.berths[0] }, label: `TAKE ${WARE_NAME[w].toUpperCase()}`, advisory: false };
    }
  }
  if (sel.berths.length === 0 && sel.ware && sel.n > 0) {
    const w = sel.ware;
    const min = MIN_SALE(w);
    if (sel.n < min)
      return {
        action: null,
        label: `${WARE_NAME[w].toUpperCase()} SELLS ${min}+ AT A TIME`,
        advisory: true,
      };
    if (p.tariffs[w].length === 0)
      return { action: null, label: `${WARE_NAME[w].toUpperCase()} IS DRY`, advisory: true };
    const paid = quote(p, w, sel.n);
    const total = sum(paid);
    const tier = bountyTierFor(total, sel.n);
    const bonus =
      tier !== null && p.bounties[tier].length > 0 ? `  ·  BOUNTY ${['I', 'II', 'III'][tier]} +${p.bounties[tier][0]}` : '';
    return {
      action: { k: 'sell', ware: w, n: sel.n },
      label: `SELL ${sel.n} ${WARE_NAME[w].toUpperCase()} → ${paid.join('+')} = ${total}${bonus}`,
      advisory: false,
    };
  }
  if (sel.berths.length >= 2) {
    if (outCards !== sel.berths.length) {
      const need = sel.berths.length - outCards;
      return {
        action: null,
        label: `BARTER ${sel.berths.length} ⇄ ${outCards} — give ${need} more`,
        advisory: true,
      };
    }
    const a: Action = {
      k: 'barter',
      berths: [...sel.berths],
      giveWares: sel.ware ? new Array(sel.n).fill(sel.ware) : [],
      givePorters: sel.porters,
    };
    if (!legal(p, seat, satchel, a))
      return { action: null, label: `BARTER ${sel.berths.length} ⇄ ${outCards} — not allowed`, advisory: true };
    return { action: a, label: `BARTER ${sel.berths.length} ⇄ ${outCards}`, advisory: false };
  }
  if (outCards > 0 || sel.berths.length > 0)
    return { action: null, label: 'PICK 2+ BERTHS TO BARTER', advisory: true };
  return { action: null, label: bestAdvice(p, satchel), advisory: true };
}

/** The greyed quote shown when nothing is selected: the best sale available, right now. */
export function bestAdvice(p: PublicState, satchel: readonly Ware[]): string {
  let best: { w: Ware; n: number; v: number } | null = null;
  for (const w of WARES) {
    const have = countOf(satchel, w);
    if (p.tariffs[w].length === 0) continue;
    for (let n = MIN_SALE(w); n <= have; n++) {
      const v = sum(quote(p, w, n));
      if (!best || v > best.v) best = { w, n, v };
    }
  }
  if (!best) return 'TAP A BERTH TO TAKE A WARE';
  return `SELL ${best.n} ${WARE_NAME[best.w].toUpperCase()} → ${best.v}`;
}

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

export class Table {
  readonly root: HTMLElement;
  private sel: Sel = emptySel();
  private last: RenderArgs | null = null;
  private pressTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onKey: (e: KeyboardEvent) => void;

  constructor(private deps: TableDeps) {
    this.root = document.createElement('div');
    this.root.className = 'stage';
    this.root.addEventListener('pointerdown', (e) => this.onDown(e));
    this.root.addEventListener('pointerup', () => this.clearPress());
    this.root.addEventListener('pointercancel', () => this.clearPress());
    this.root.addEventListener('click', (e) => this.onClick(e));
    this.onKey = (e) => this.key(e);
    window.addEventListener('keydown', this.onKey);
  }

  destroy(): void {
    this.clearPress();
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  private clearPress(): void {
    if (this.pressTimer !== null) clearTimeout(this.pressTimer);
    this.pressTimer = null;
  }

  clearSelection(): void {
    this.sel = emptySel();
    if (this.last) this.render(this.last);
  }

  /** Long-press a satchel stack to select the whole set at once. */
  private onDown(e: PointerEvent): void {
    const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-stack]');
    if (!el || !this.last) return;
    const w = el.dataset.stack as Ware;
    this.clearPress();
    this.pressTimer = setTimeout(() => {
      this.pressTimer = null;
      const have = countOf(this.last!.satchel, w);
      this.sel = { ...emptySel(), ware: w, n: have };
      this.deps.sfx('tap');
      this.render(this.last!);
    }, 350);
  }

  private onClick(e: Event): void {
    const t = e.target as HTMLElement | null;
    if (!t || !this.last) return;
    const a = this.last;
    const berth = t.closest<HTMLElement>('[data-berth]');
    const stack = t.closest<HTMLElement>('[data-stack]');
    const gang = t.closest<HTMLElement>('[data-gang]');
    const quoteBtn = t.closest<HTMLElement>('[data-commit]');
    const quit = t.closest<HTMLElement>('[data-quit]');
    const help = t.closest<HTMLElement>('[data-help]');

    if (quit) {
      this.deps.onQuit();
      return;
    }
    if (help) {
      this.deps.onHelp();
      return;
    }
    if (quoteBtn) {
      const q = readSelection(a.p, a.you, a.satchel, this.sel);
      if (q.action && a.yourTurn) {
        this.deps.submit(q.action);
        this.sel = emptySel();
      } else this.deps.sfx('tap');
      return;
    }
    if (berth) {
      const b = Number(berth.dataset.berth);
      const c = a.p.trestle[b];
      if (c === null) return;
      this.deps.sfx('tap');
      if (c === PORTER) {
        this.sel = this.sel.hire ? emptySel() : { ...emptySel(), hire: true };
      } else {
        const has = this.sel.berths.includes(b);
        const berths = has ? this.sel.berths.filter((x) => x !== b) : [...this.sel.berths, b];
        this.sel = { ...this.sel, hire: false, berths };
      }
      this.render(a);
      return;
    }
    if (stack) {
      const w = stack.dataset.stack as Ware;
      const have = countOf(a.satchel, w);
      this.deps.sfx('tap');
      if (this.sel.ware !== w) this.sel = { ...this.sel, hire: false, ware: w, n: 1 };
      else {
        const n = this.sel.n + 1;
        this.sel = n > have ? { ...this.sel, ware: null, n: 0 } : { ...this.sel, n };
      }
      this.render(a);
      return;
    }
    if (gang) {
      const have = a.p.gang[a.you];
      if (have === 0) return;
      this.deps.sfx('tap');
      const n = this.sel.porters + 1;
      this.sel = { ...this.sel, hire: false, porters: n > have ? 0 : n };
      this.render(a);
    }
  }

  private key(e: KeyboardEvent): void {
    if (!this.last) return;
    const a = this.last;
    if (e.key === 'Escape') {
      this.sel = emptySel();
      this.render(a);
      return;
    }
    if (e.key === ' ' || e.key === 'Enter') {
      const q = readSelection(a.p, a.you, a.satchel, this.sel);
      if (q.action && a.yourTurn) {
        e.preventDefault();
        this.deps.submit(q.action);
        this.sel = emptySel();
      }
      return;
    }
    const berthKeys = ['1', '2', '3', '4', '5'];
    const stackKeys = ['q', 'w', 'e', 'r', 't'];
    if (berthKeys.includes(e.key)) {
      const el = this.root.querySelector<HTMLElement>(`[data-berth="${berthKeys.indexOf(e.key)}"]`);
      el?.click();
      return;
    }
    const si = stackKeys.indexOf(e.key.toLowerCase());
    if (si >= 0) {
      const held = WARES.filter((w) => countOf(a.satchel, w) > 0);
      const w = held[si];
      if (w) this.root.querySelector<HTMLElement>(`[data-stack="${w}"]`)?.click();
    }
  }

  say(msg: string): void {
    const el = this.root.querySelector<HTMLElement>('.ticker');
    const hud = this.root.querySelector<HTMLElement>('.hudticker');
    if (el) el.textContent = msg;
    if (hud) hud.textContent = msg;
  }

  render(a: RenderArgs): void {
    this.last = a;
    const { p, you } = a;
    const them = other(you);
    const q = readSelection(p, you, a.satchel, this.sel);
    const doomed = doomedChit(p);
    const dry = dryCount(p);
    const yourScore = scoreOf(p, you).total;
    const theirScore = scoreOf(p, them).total;
    const read = provableRead(p, you);

    this.root.innerHTML = `
      <div class="hud">
        <div>
          <div class="pips" aria-label="pennants">${'●'.repeat(a.pennants[you])}${'○'.repeat(Math.max(0, 2 - a.pennants[you]))}</div>
          <div class="scores"><span class="you">you ${yourScore}</span> — <span class="them">them ${theirScore}</span></div>
        </div>
        <div class="hudticker"></div>
        <div class="meta">
          <div>crate ${p.crateCount}</div>
          <div class="dryline">${dry} of 3 dry</div>
          ${you === p.starter ? '' : '<div>+2 tare</div>'}
        </div>
      </div>

      <div class="rival">
        <div class="who">
          <span>${esc(a.names[them] ?? 'Rival')}</span>
          <span class="backs" aria-label="${p.satchelCount[them]} cards">${'<span class="back"></span>'.repeat(Math.min(6, p.satchelCount[them]))}</span>
          <span title="porters">${WARE_GLYPH.porter}${p.gang[them]}</span>
          <span class="turnflag ${a.yourTurn ? '' : 'live'}">${a.yourTurn ? 'your turn' : 'their turn'}</span>
        </div>
        <div class="read">${read ? esc(read) : `They hold ${p.satchelCount[them]} card${p.satchelCount[them] === 1 ? '' : 's'}.`}</div>
      </div>

      <div class="tariffs grid5">${WARES.map((w) => this.tariffCol(p, w, doomed?.ware === w)).join('')}</div>

      <div class="zone trestle">
        <div class="zlabel"><span>Trestle</span><span>${p.crateTop ? `next: ${p.crateTop === PORTER ? 'porter' : WARE_NAME[p.crateTop]}` : 'crate empty'}</span></div>
        <div class="grid5">${Array.from({ length: BERTHS }, (_, b) => this.berth(p, b)).join('')}</div>
      </div>

      <button class="gangbar ${this.sel.porters > 0 ? 'out' : ''}" data-gang type="button">
        <span>${WARE_GLYPH.porter}</span><span class="n">${p.gang[you]}</span><span>porters</span>
        <span class="hint">${this.sel.porters > 0 ? `${this.sel.porters} marked to spend` : 'tap to spend in a barter'}</span>
      </button>

      <div class="zone satchel">
        <div class="zlabel"><span>Your satchel</span><span>${a.satchel.length} / ${SATCHEL_LIMIT}</span></div>
        <div class="grid5">${this.satchelSlots(p, a.satchel)}</div>
      </div>

      <div class="ticker">${esc(a.note)}</div>

      <div class="actionbar">
        <button class="quote ${!a.yourTurn ? 'waiting' : q.advisory ? 'advisory' : ''}" data-commit type="button" ${q.action && a.yourTurn ? '' : 'disabled'}>
          ${a.yourTurn ? esc(q.label) : 'WAITING FOR YOUR RIVAL…'}
        </button>
        <button class="iconbtn" data-help type="button" aria-label="How to play">?</button>
        <button class="iconbtn" data-quit type="button" aria-label="Leave the deal">✕</button>
      </div>`;
    this.say(a.note);
  }

  private tariffCol(p: PublicState, w: Ware, doomed: boolean): string {
    const left = p.tariffs[w];
    const isDry = left.length === 0;
    const isLast = left.length === 1;
    const price = isDry ? 'DRY' : String(left[0]);
    const rest = left.slice(1, 4).join(' ');
    return `<div class="tcol ${isDry ? 'dry' : ''} ${isLast ? 'last' : ''} ${doomed ? 'doomed' : ''}"
        aria-label="${WARE_NAME[w]}: ${isDry ? 'dry' : `pays ${left[0]}, then ${left.slice(1).join(', ') || 'nothing'}`}">
      <span class="glyph" style="color:var(--w-${w})">${WARE_GLYPH[w]}</span>
      <span class="price" style="background:${isDry ? 'transparent' : `var(--w-${w})`}">${price}</span>
      <span class="rest">${isLast ? 'LAST' : rest}</span>
      <span class="wname">${WARE_NAME[w]}</span>
    </div>`;
  }

  private berth(p: PublicState, b: number): string {
    const c: Card | null = p.trestle[b];
    if (c === null) return `<div class="slot empty" aria-hidden="true"></div>`;
    const marked = this.sel.berths.includes(b) || (this.sel.hire && c === PORTER);
    if (c === PORTER) {
      return `<button class="slot porter ${marked ? 'in' : ''}" data-berth="${b}" type="button" aria-label="Porter, berth ${b + 1}">
        ${marked ? '<span class="mark">▲</span>' : ''}
        <span class="cglyph">${WARE_GLYPH.porter}</span><span class="cname">Porter</span>
      </button>`;
    }
    const top = p.tariffs[c][0];
    return `<button class="slot ${marked ? 'in' : ''}" data-berth="${b}" type="button" aria-label="${WARE_NAME[c]}, berth ${b + 1}, pays ${top ?? 0}">
      ${marked ? '<span class="mark">▲</span>' : ''}
      <span class="cglyph" style="color:var(--w-${c})">${WARE_GLYPH[c]}</span>
      <span class="cname">${WARE_NAME[c]}</span>
      <span class="cprice">${top === undefined ? '—' : top}</span>
    </button>`;
  }

  private satchelSlots(p: PublicState, satchel: readonly Ware[]): string {
    const held = WARES.filter((w) => countOf(satchel, w) > 0);
    const cells = held.map((w) => {
      const n = countOf(satchel, w);
      const marked = this.sel.ware === w ? this.sel.n : 0;
      const top = p.tariffs[w][0];
      return `<button class="slot ${marked > 0 ? 'out' : ''}" data-stack="${w}" type="button"
          aria-label="${n} ${WARE_NAME[w]} in your satchel${marked > 0 ? `, ${marked} marked` : ''}">
        ${marked > 0 ? `<span class="mark">▼${marked}</span>` : ''}
        <span class="count">${n}</span>
        <span class="cglyph" style="color:var(--w-${w})">${WARE_GLYPH[w]}</span>
        <span class="cname">${WARE_NAME[w]}</span>
        <span class="cprice">${top === undefined ? 'dry' : top}</span>
      </button>`;
    });
    while (cells.length < 5) cells.push('<div class="slot empty" aria-hidden="true"></div>');
    return cells.join('');
  }

  /**
   * Chits arc from the tariff column to the score. Every one carries a setTimeout backstop as well
   * as its transition, because a tab that goes to the background mid-arc never fires transitionend
   * and would strand the element on top of the board for ever.
   */
  flyChits(ware: Ware, chits: readonly number[]): void {
    const from = this.root.querySelector<HTMLElement>(`.tcol[aria-label^="${WARE_NAME[ware]}"]`);
    const to = this.root.querySelector<HTMLElement>('.hud .scores');
    if (!from || !to) return;
    const a = from.getBoundingClientRect();
    const b = to.getBoundingClientRect();
    chits.forEach((c, i) => {
      const el = document.createElement('div');
      el.className = 'flychit';
      el.textContent = String(c);
      el.style.left = `${a.left + a.width / 2 - 12}px`;
      el.style.top = `${a.top + 8}px`;
      document.body.append(el);
      const kill = (): void => el.remove();
      setTimeout(() => {
        el.style.transform = `translate(${b.left - a.left}px, ${b.top - a.top}px) scale(0.7)`;
        el.style.opacity = '0';
      }, 20 + i * 40);
      el.addEventListener('transitionend', kill, { once: true });
      setTimeout(kill, 400 + i * 40);
    });
  }

  bump(): void {
    this.root.classList.add('shake');
    setTimeout(() => this.root.classList.remove('shake'), 320);
  }
}

export { TARIFF };
